import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useAppSelector } from "@/hooks/redux";
import { UpgradePrompt } from "@/components/dashboard/UpgradePrompt";
import { useTranslation } from "react-i18next";
import { featureService } from "@/services/featureService";
import type { FeaturePlanInfo } from "@/services/featureService";
import { TicketX } from "lucide-react";

interface FeatureGuardProps {
  children: React.ReactNode;
  requiredFeature: string;
  fallbackPath?: string;
  showUpgradePrompt?: boolean;
}

export const FeatureGuard: React.FC<FeatureGuardProps> = ({
  children,
  requiredFeature,
  fallbackPath = "/dashboard",
  showUpgradePrompt = true,
}) => {
  const { user } = useAppSelector((state) => state.auth);
  const { t } = useTranslation();
  const [featurePlanInfo, setFeaturePlanInfo] =
    useState<FeaturePlanInfo | null>(null);
  const [loading, setLoading] = useState(true);

  // Récupérer les informations du plan requis depuis la base de données
  useEffect(() => {
    const fetchFeaturePlanInfo = async () => {
      try {
        setLoading(true);
        const planInfo =
          await featureService.getRequiredPlanForFeature(requiredFeature);
        setFeaturePlanInfo(planInfo);
      } catch (error) {
        console.error("Error fetching feature plan info:", error);
        // Fallback vers les valeurs hardcodées en cas d'erreur
        // setFeaturePlanInfo({
        //   featureName: t("upgrade.feature"),
        //   requiredPlan: "Pro",
        //   requiredPlanPrice: 119,
        //   allPlansWithFeature: [],
        // });
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturePlanInfo();
  }, [requiredFeature, t]);

  // Si l'utilisateur n'est pas connecté, rediriger vers la connexion
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Vérifier si l'utilisateur a la fonctionnalité requise
  const hasFeature = user.enabledFeatures?.includes(requiredFeature) || false;

  if (hasFeature) {
    return <>{children}</>;
  }

  // Si on ne doit pas afficher le prompt d'upgrade, rediriger
  if (!showUpgradePrompt) {
    return <Navigate to={fallbackPath} replace />;
  }

  // Afficher un loader pendant le chargement
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  // Si pas d'information sur le plan, afficher un message d'erreur
  if (!featurePlanInfo) {
    return (
      <div className="flex items-center justify-center min-h-[400px] flex-1 ">
        <div className="text-center flex flex-col items-center gap-2">
          <TicketX className="h-16 w-16 text-muted-foreground dark:text-muted-foreground font-light" />
          <p className="text-muted-foreground">
            {t("upgrade.featurePlanEmpty")}
          </p>
        </div>
      </div>
    );
  }

  // Déterminer la description de la fonctionnalité
  const getFeatureDescription = (feature: string) => {
    switch (feature) {
      case "code_generation":
        return t("upgrade.codeGenerationDescription");
      case "variable_library":
        return t("upgrade.variableLibraryDescription");
      case "code_editor":
        return t("upgrade.codeEditorDescription");
      default:
        return t("upgrade.defaultDescription");
    }
  };

  // Afficher le prompt d'upgrade avec les données dynamiques
  return (
    <UpgradePrompt
      featureName={featurePlanInfo.featureName}
      featureDescription={getFeatureDescription(requiredFeature)}
      currentPlan={user.subscriptionPlan || "Starter"}
      requiredPlans={featurePlanInfo.allPlansWithFeature.map(
        (plan) => plan.name,
      )}
    />
  );
};

// Composant pour masquer conditionnellement des éléments
interface FeatureWrapperProps {
  children: React.ReactNode;
  requiredFeature: string;
  fallback?: React.ReactNode;
}

export const FeatureWrapper: React.FC<FeatureWrapperProps> = ({
  children,
  requiredFeature,
  fallback = null,
}) => {
  const { user } = useAppSelector((state) => state.auth);

  if (!user) {
    return <>{fallback}</>;
  }

  const hasFeature = user.enabledFeatures?.includes(requiredFeature) || false;

  if (hasFeature) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
};
